import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import axiosInstance from "../../api/axiosInstance";
import { buttonClass, errorMessage, inputClass, PAGE_SIZE } from "./catalogUtils";

export function ProductRelation({
  kind, label, value, selectedName, required, disabled, onChange,
  endpoint = "/api/superadmin/catalog/products/options/", idPrefix = "product", emptyLabel = "No brand",
}) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [options, setOptions] = useState([]);
  const [links, setLinks] = useState({ next: null, previous: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const input = useRef(null);
  const listId = `${idPrefix}-${kind}-options`;
  const expanded = open && !disabled;
  const pending = loading || search.trim() !== query;
  const choices = [
    ...(!required ? [{ id: "", name: emptyLabel }] : []),
    ...(!pending && !error ? options : []),
  ];

  function showOptions() {
    setSearch("");
    setPage(1);
    setActiveIndex(0);
    setOpen(true);
  }
  function selectOption(option) {
    onChange(String(option.id), option.name);
    setOpen(false);
    setSearch("");
  }
  function moveOption(index) {
    setActiveIndex(index);
    document.getElementById(`${listId}-${index}`)?.scrollIntoView?.({ block: "nearest" });
  }

  useEffect(() => {
    if (search.trim() === query) return;
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search, query]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance.get(endpoint, {
      params: { kind, q: query, page, page_size: PAGE_SIZE },
    }).then(({ data }) => {
      if (cancelled) return;
      if (!Array.isArray(data.results)) throw new Error("Invalid product options response");
      setOptions(data.results);
      setLinks({ next: data.next, previous: data.previous });
    }).catch((requestError) => {
      if (!cancelled) {
        setOptions([]);
        setError(requestError.message === "Invalid product options response"
          ? "The server returned invalid options. Please retry." : errorMessage(requestError));
      }
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [endpoint, kind, query, page, revision]);

  return (
    <div className="relative" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
    }}>
      <label htmlFor={`${idPrefix}-${kind}`} className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      <div className="relative">
        <input ref={input} id={`${idPrefix}-${kind}`} role="combobox" required={required}
          aria-autocomplete="list" aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          aria-activedescendant={expanded && choices[activeIndex] ? `${listId}-${activeIndex}` : undefined}
          autoComplete="off" placeholder={`Select or search ${label.toLowerCase()}...`}
          value={expanded ? search : (value ? selectedName : "")} disabled={disabled}
          onFocus={() => { if (!open) showOptions(); }} onClick={() => { if (!open) showOptions(); }}
          onChange={(event) => {
            setSearch(event.target.value); setActiveIndex(0); setOpen(true); onChange("", "");
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              if (!expanded) showOptions();
              else moveOption(Math.max(0, Math.min(choices.length - 1, activeIndex + (event.key === "ArrowDown" ? 1 : -1))));
            } else if (event.key === "Enter" && expanded) {
              event.preventDefault();
              if (choices[activeIndex]) selectOption(choices[activeIndex]);
            } else if (event.key === "Escape" && expanded) {
              event.preventDefault(); event.stopPropagation(); setOpen(false);
            }
          }} className={`${inputClass} pr-10`} />
        <button type="button" aria-label={`Toggle ${label.toLowerCase()} options`} disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (expanded) setOpen(false);
            else { input.current?.focus(); showOptions(); }
          }} className="absolute inset-y-0 right-0 px-3 text-slate-400 disabled:opacity-50">
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {expanded && (
        <div className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg">
          <ul id={listId} role="listbox" aria-label={`${label} options`}>
            {choices.map((option, index) => (
              <li key={option.id} id={`${listId}-${index}`} role="option"
                aria-selected={String(option.id) === String(value)}
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => setActiveIndex(index)} onClick={() => selectOption(option)}
                className={`cursor-pointer rounded-md px-3 py-2 text-sm text-slate-700 ${index === activeIndex ? "bg-blue-50" : ""}`}>
                {option.name}
              </li>
            ))}
          </ul>
          {pending && <p role="status" className="p-3 text-xs text-slate-500">Loading {label.toLowerCase()} options...</p>}
          {!pending && !error && !options.length && <p role="status" className="p-3 text-xs text-slate-500">No matching {label.toLowerCase()} options.</p>}
          {(links.next || links.previous) && !pending && !error && (
            <div className="flex items-center justify-between gap-2 border-t border-slate-100 p-2 text-xs text-slate-500">
              <button type="button" disabled={disabled || !links.previous}
                onClick={() => { setPage((value) => value - 1); setActiveIndex(0); input.current?.focus(); }}
                onMouseDown={(event) => event.preventDefault()} className="disabled:opacity-40">Previous {label.toLowerCase()} options</button>
              <button type="button" disabled={disabled || !links.next}
                onClick={() => { setPage((value) => value + 1); setActiveIndex(0); input.current?.focus(); }}
                onMouseDown={(event) => event.preventDefault()} className="disabled:opacity-40">Next {label.toLowerCase()} options</button>
            </div>
          )}
        </div>
      )}
      {error && <div role="alert" className="mt-1 text-xs text-red-600">{error}
        <button type="button" disabled={disabled} onClick={() => setRevision((value) => value + 1)}
          className="ml-2 underline">Retry {label.toLowerCase()} options</button>
      </div>}
    </div>
  );
}

export default function CatalogProductForm({ product, busy, error, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    sku: "", name: "", subcategory: "", unit_type: "", part_number: "",
    description: "", is_active: false,
    ...product,
    brand: product?.brand ?? "",
    price: product?.price ?? "", height: product?.height ?? "",
    width: product?.width ?? "", length: product?.length ?? "",
    specifications: JSON.stringify(product?.specifications ?? {}, null, 2),
  }));
  const [jsonError, setJsonError] = useState("");
  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }
  function submit(event) {
    event.preventDefault();
    let specifications;
    try {
      specifications = JSON.parse(form.specifications);
    } catch {
      setJsonError("Specifications must be valid JSON.");
      return;
    }
    if (!specifications || Array.isArray(specifications) || typeof specifications !== "object") {
      setJsonError("Specifications must be a JSON object.");
      return;
    }
    setJsonError("");
    onSave({
      sku: form.sku.trim(), name: form.name.trim(), subcategory: Number(form.subcategory),
      brand: form.brand ? Number(form.brand) : null, unit_type: Number(form.unit_type),
      part_number: form.part_number.trim(), description: form.description, specifications,
      price: form.price === "" ? null : form.price,
      height: form.height === "" ? null : form.height,
      width: form.width === "" ? null : form.width,
      length: form.length === "" ? null : form.length,
      is_active: form.is_active,
    });
  }

  return (
    <form aria-label={product ? "Edit product" : "Create product"} onSubmit={submit}>
      {error && <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      <fieldset disabled={busy} className="grid min-w-0 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="product-name" className="mb-1 block text-sm font-medium text-slate-700">Product name</label>
          <input data-initial-focus id="product-name" required maxLength={455} value={form.name}
            onChange={(event) => change("name", event.target.value)} className={inputClass} />
        </div>
        <div>
          <label htmlFor="product-sku" className="mb-1 block text-sm font-medium text-slate-700">SKU</label>
          <input id="product-sku" required maxLength={100} value={form.sku}
            onChange={(event) => change("sku", event.target.value)} className={inputClass} />
        </div>
        {[
          ["subcategory", "Subcategory", true, form.category_name ? `${form.category_name} > ${form.subcategory_name}` : form.subcategory_name],
          ["brand", "Brand", false, form.brand_name],
          ["unit_type", "Unit", true, form.unit_type_name],
        ].map(([kind, label, required, selectedName]) => (
          <ProductRelation key={kind} kind={kind} label={label} required={required}
            value={form[kind]} selectedName={selectedName} disabled={busy}
            onChange={(value, name) => setForm((current) => ({
              ...current, [kind]: value, [`${kind}_name`]: name,
              ...(kind === "subcategory" ? { category_name: "" } : {}),
            }))} />
        ))}
        <div>
          <label htmlFor="product-part-number" className="mb-1 block text-sm font-medium text-slate-700">Part number</label>
          <input id="product-part-number" maxLength={150} value={form.part_number}
            onChange={(event) => change("part_number", event.target.value)} className={inputClass} />
        </div>
        {[
          ["price", "Reference price", "0", "0.01", "999999999999.99"],
          ["height", "Height (cm)", "0.001", "0.001", "999999999.999"],
          ["width", "Width (cm)", "0.001", "0.001", "999999999.999"],
          ["length", "Length (cm)", "0.001", "0.001", "999999999.999"],
        ].map(([field, label, min, step, max]) => (
          <div key={field}>
            <label htmlFor={`product-${field}`} className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
            <input id={`product-${field}`} type="number" min={min} max={max} step={step} value={form[field]}
              onChange={(event) => change(field, event.target.value)} className={inputClass} />
          </div>
        ))}
        <div className="sm:col-span-2">
          <label htmlFor="product-description" className="mb-1 block text-sm font-medium text-slate-700">Description</label>
          <textarea id="product-description" rows={3} value={form.description}
            onChange={(event) => change("description", event.target.value)} className={inputClass} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="product-specifications" className="mb-1 block text-sm font-medium text-slate-700">Specifications (JSON object)</label>
          <textarea id="product-specifications" rows={3} value={form.specifications}
            aria-invalid={Boolean(jsonError)} aria-describedby={jsonError ? "product-json-error" : undefined}
            onChange={(event) => { change("specifications", event.target.value); setJsonError(""); }}
            className={`${inputClass} font-mono`} />
          {jsonError && <p id="product-json-error" role="alert" className="mt-1 text-sm text-red-600">{jsonError}</p>}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={form.is_active} onChange={(event) => change("is_active", event.target.checked)} />
          Active
        </label>
      </fieldset>
      <div className="mt-5 flex justify-end gap-2 border-t border-slate-100 pt-4">
        <button type="submit" disabled={busy || !form.name.trim() || !form.sku.trim() || !form.subcategory || !form.unit_type}
          className={`${buttonClass} bg-blue-500 text-white hover:bg-blue-600`}>{busy ? "Saving..." : "Save Product"}</button>
        <button type="button" disabled={busy} onClick={onClose}
          className={`${buttonClass} border border-slate-200 text-slate-600 hover:bg-slate-50`}>Cancel</button>
      </div>
    </form>
  );
}
